import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

export interface SegmentOption<T extends string> {
    value: T
    label: ReactNode
}

interface SegmentedProps<T extends string> {
    options: Array<SegmentOption<T>>
    value: T
    onChange: (value: T) => void
    label: string
    size?: 'sm' | 'md'
    className?: string
}

export const Segmented = <T extends string>({ options, value, onChange, label, size = 'sm', className }: SegmentedProps<T>): ReactNode => {
    return (
        <div
            role='group'
            aria-label={label}
            className={cn('inline-flex max-w-full items-center gap-0.5 overflow-x-auto rounded-lg border border-border bg-background/60 p-0.5 no-scrollbar', className)}
        >
            {options.map(option => {
                const active = option.value === value

                return (
                    <button
                        key={option.value}
                        type='button'
                        aria-pressed={active}
                        onClick={() => onChange(option.value)}
                        className={cn(
                            'shrink-0 rounded-md font-medium transition-colors',
                            size === 'sm' ? 'h-7 px-2.5 text-xs' : 'h-8 px-3 text-[13px]',
                            active
                                ? 'bg-secondary text-foreground shadow-[inset_0_0_0_1px_var(--border)]'
                                : 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground'
                        )}
                    >
                        {option.label}
                    </button>
                )
            })}
        </div>
    )
}
